/* THE FOOD WEB (LS-519, after Caves of Qud; Vijay 2026-09-29: "a cave of qud level simulation").
 *
 * Nothing in the wilds ate. Creatures chased the player and idled, the sow grazed thorn and the pack ate rosters,
 * and that was the whole of the country's hunger. Now every kind that eats has a diet and a hunger that fills over a
 * few minutes; once it is hungry, and the player is not the business at hand, it goes for what it eats within sight:
 *   - a grazer crops the meadow (a grazed tile is cut grass, which grows back on the grass's own clock);
 *   - a hunter goes for the smaller kinds it takes, which bolt from it;
 *   - a scavenger goes for remains -- what a death leaves lying for a while -- and eats them down.
 * A meal ends the hunger; a grazed tile ends some of it. What a hunter kills is not the player's kill: it leaves its
 * remains and nothing else. */
import { T, TS } from '../art.ts';

export type Diet = { grazes?: number[]; hunts?: string[]; scavenges?: boolean };
export const DIETS: Record<string, Diet> = {
  shellback: { grazes: [T.MOSS] },
  screehopper: { grazes: [T.MOSS] },
  mossback: { grazes: [T.MOSS] },
  charger: { grazes: [T.MOSS], scavenges: true },      // the boar takes what it finds
  tidecrab: { scavenges: true },
  stormgull: { scavenges: true, hunts: ['tidecrab'] },
  sporeling: { scavenges: true },                       // a decomposer: what it eats goes back to the ground as spores
  duskling: { hunts: ['sporeling', 'screehopper'] },
  sunbasker: { hunts: ['sporeling', 'duskling'] },
  eel: { hunts: ['tidecrab', 'mossback'] },
};

/** Morale (LS-520): how long a wounded creature bolts, and how often one with nothing near mends a point. */
export const ROUT_FRAMES = 180, MEND_FRAMES = 600;
/** Companions (LS-521): how long a creature fed from the hand is yours, and how far round you it guards. */
export const FRIEND_FRAMES = 60 * 180, GUARD_REACH = 80;
/* STANDING (LS-522, after Caves of Qud's factions): each kind keeps a count of what you have done to it -- a kill
 * takes one off, a feeding from the hand puts one on. At FEARED or under the kind flees from you on sight; at
 * PEACEABLE or over it goes about its business and does not come at you unless you strike it. Kept in the save. */
export const FEARED = -8, PEACEABLE = 2, SHY_REACH = 96, PROVOKED_FRAMES = 1200;
/* BREEDING (LS-524): two well-fed creatures of a kind within five tiles of each other bear a young one, at most one
 * every two minutes each, while their chunk holds fewer than six. A fed country fills; a hunted one thins. */
export const BREED_FRAMES = 60 * 120, BREED_FED = 0.25, BREED_REACH = 80, CHUNK_CAP = 6;
/* TRACKING (LS-527): past AWARE px a creature does not know where the body is; it follows the body's scent if it
 * stands on some (above SCENT_TRAIL), and otherwise goes about its own business. */
export const AWARE = 112, SCENT_TRAIL = 0.04;
/* THIRST AND WEATHER (LS-528): thirst fills over five minutes and a thirsty creature (over THIRSTY) walks to water
 * within DRINK_SIGHT tiles and drinks; in rain over DOWNPOUR a creature takes cover in a thicket or under a tree within
 * SHELTER_SIGHT tiles and stays there while it falls. */
export const THIRST_FRAMES = 60 * 300, THIRSTY = 0.6, DRINK_SIGHT = 10, DRINK_FRAMES = 90, DOWNPOUR = 0.6, SHELTER_SIGHT = 6;
export function standingOf(state: any, kind: string): number { const v = state && state.rep ? state.rep[kind] : 0; return Number.isFinite(v) ? v : 0; }
export function nudgeStanding(state: any, kind: string, by: number) { if (!state) return; const r = (state.rep ||= {}); r[kind] = Math.max(-20, Math.min(20, (Number.isFinite(r[kind]) ? r[kind] : 0) + by)); }
/** From sated to starving, in frames: three minutes. */
export const HUNGER_FRAMES = 60 * 180;
/** Hungry from here. */
export const HUNGRY = 0.5;
/** How far a creature looks for food, in tiles. */
export const SIGHT = 7;
/** A creature minds the player instead within this many px (unless it is starving). */
export const MIND_PLAYER = 72;
/** Frames a mouthful takes: a crop of grass, a bite of remains. */
export const GRAZE_FRAMES = 80, BITE_FRAMES = 110;
/** What a crop of grass takes off the hunger; remains and a kill take all of it. */
export const GRAZE_MEAL = 0.15;
/** How long remains lie, in frames, and how many bites they hold. */
export const REMAINS_FRAMES = 60 * 180, REMAINS_BITES = 3, REMAINS_CAP = 32;

/** A creature's starting hunger, from where it was made -- never from its own rng stream, which every later step of
 *  its life draws from (drawing here would move every creature's path in every world). */
export function seedHunger(e: any): number {
  const h = (Math.imul(Math.floor(e.x) * 73856093 ^ Math.floor(e.y) * 19349663, 2654435761) >>> 0) / 4294967296;
  return h * 0.7;
}

export type Meal = { kind: 'grass'; tx: number; ty: number } | { kind: 'remains'; r: any } | { kind: 'prey'; e: any } | { kind: 'bait'; it: any };
/** Meat on the ground: what hunts and what scavenges will come to it (LS-519, slice two). */
export const BAIT = new Set(['meat', 'cookedmeat']);

/** The nearest thing this creature eats, within its sight, or null. */
export function findMeal(game: any, e: any, diet: Diet): Meal | null {
  const reach = SIGHT * TS;
  let best: Meal | null = null, bd = reach;
  if (diet.hunts) for (const o of game.enemies || []) {
    if (o === e || o.dead || o.hiding || o.spawnFx > 0 || !diet.hunts.includes(o.type)) continue;
    const d = Math.hypot(o.cx - e.cx, o.cy - e.cy) * (o.hp < o.maxHp ? 0.6 : 1);   // the wounded first (LS-520)
    if (d < bd) { bd = d; best = { kind: 'prey', e: o }; }
  }
  if (diet.scavenges) for (const r of game.remains || []) {
    if (r.bites <= 0) continue;
    const d = Math.hypot(r.x - e.cx, r.y - e.cy);
    if (d < bd) { bd = d; best = { kind: 'remains', r }; }
  }
  if (diet.hunts || diet.scavenges) for (const it of game.items || []) {
    if (it.dead || !BAIT.has(it.kind) || it.offered) continue;
    const d = Math.hypot(it.x + 4 - e.cx, it.y + 4 - e.cy);
    if (d < bd + TS * 2) { bd = d; best = { kind: 'bait', it }; }   // bait is smelt from further than a body is seen
  }
  if (diet.grazes && !best) {
    const cx = Math.floor(e.cx / TS), cy = Math.floor(e.cy / TS);
    /* the nearest grazing, ring by ring, so a meadow is cropped from the edge a body stands at, not picked at random */
    for (let r = 0; r <= SIGHT && !best; r++) {
      for (let dy = -r; dy <= r && !best; dy++) for (let dx = -r; dx <= r; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
        const v = game.tileAt(cx + dx, cy + dy);
        if (v !== undefined && diet.grazes.includes(v) && !(game.systems && game.systems.burning(cx + dx, cy + dy))) { best = { kind: 'grass', tx: cx + dx, ty: cy + dy }; break; }
      }
    }
  }
  return best;
}

/** Where a meal is, in world px. */
export function mealAt(m: Meal): [number, number] {
  return m.kind === 'grass' ? [m.tx * TS + TS / 2, m.ty * TS + TS / 2] : m.kind === 'remains' ? [m.r.x, m.r.y] : m.kind === 'bait' ? [m.it.x + 4, m.it.y + 4] : [m.e.cx, m.e.cy];
}
/** Is the meal still there to be had? */
export function mealLive(game: any, m: Meal, diet: Diet): boolean {
  if (m.kind === 'grass') { const v = game.tileAt(m.tx, m.ty); return v !== undefined && !!diet.grazes && diet.grazes.includes(v); }
  if (m.kind === 'remains') return m.r.bites > 0 && (game.remains || []).includes(m.r);
  if (m.kind === 'bait') return !m.it.dead && (game.items || []).includes(m.it);
  return !m.e.dead && (game.enemies || []).includes(m.e);
}
